import compact from "lodash/compact";
import filter from "lodash/filter";
import find from "lodash/find";
import flatten from "lodash/flatten";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import map from "lodash/map";
import reverse from "lodash/reverse";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";
import day from "dayjs";
import {
  LeftOutlined,
  InfoCircleOutlined,
  RightOutlined,
  CalendarOutlined,
  MailOutlined,
  SyncOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  List,
  Modal,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import { useAuth } from "@clerk/clerk-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePrevious } from "react-use";

import { styles } from "@evolved/constants";

import { Activity } from "components/lists/activities/list";
import { Metadata } from "components/lists/metadata";
import { useContacts } from "data/use-contacts";
import { useMe } from "data/use-me";
import { useOrganization } from "data/use-organization";
import { useUsers } from "data/use-users";
import { detailed } from "utils/format-date";

import { RangeFilter } from "./range-filter";
import classes from "./style.module.css";
import { useModal } from "hooks/use-modal";
import { CreateCalendarEventModal } from "components/modals/create-calendar-event-modal";

const { RangePicker } = DatePicker;
const { PRIMARY_TEXT } = styles;

const getDefaultRange = () => [
  day().subtract(14, "days").startOf("day"),
  day().endOf("day"),
];

const getDefaultCalendarRange = () => [
  day().subtract(14, "days").startOf("day"),
  day().add(14, "days").endOf("day"),
];

const isSame = (a, b) => {
  return (
    a &&
    b &&
    a.length === b.length &&
    a.every(({ _id }) => b.some((second) => _id === second._id))
  );
};

const isSameRange = (a, b) => {
  return (
    a &&
    b &&
    a.length === b.length &&
    a.every((value, index) => value?.isSame?.(b[index]))
  );
};

const getRangeKey = (range) => {
  return (range || []).map((value) => value?.valueOf?.() || "").join(":");
};

const getInitialRange = ({ activities = [], allowFuture }) => {
  const defaultRange = allowFuture ? getDefaultCalendarRange() : getDefaultRange();

  const sortedActivities = sortBy(
    (activities || []).filter((activity) => !isNil(activity?.performedOn)),
    (activity) => -activity.performedOn
  );
  const boundaryActivity = sortedActivities[Math.min(sortedActivities.length, 5) - 1];

  if (!boundaryActivity) {
    return defaultRange;
  }

  const earliestStart = day.unix(boundaryActivity.performedOn).startOf("day");

  if (earliestStart.isAfter(defaultRange[0])) {
    return defaultRange;
  }

  return [earliestStart, defaultRange[1]];
};

const getEmailParams = ({ contacts, page, range, user }) => {
  const params = new URLSearchParams();

  if (page) params.append("page", page);

  const start = range[0].startOf("day").toISOString();
  const end = range[1].endOf("day").toISOString();

  params.append("start", start);
  params.append("end", end);

  params.append(
    "emails",
    contacts
      .filter(({ email }) => !isEmpty(email))
      .map(({ email }) => email)
      .join(",")
  );
  params.append("user", user._id);

  return params.toString();
};

const getEventParams = ({ contacts, page, range, user }) => {
  const params = new URLSearchParams();

  if (page) params.append("page", page);

  const start = range[0].startOf("day").toISOString();
  const end = range[1].endOf("day").toISOString();

  params.append("start", start);
  params.append("end", end);

  params.append(
    "emails",
    contacts
      .filter(({ email }) => !isEmpty(email))
      .map(({ email }) => email)
      .join(",")
  );
  params.append("user", user._id);

  return params.toString();
};

const Persons = ({ label, persons = [] }) => {
  if (isEmpty(persons)) {
    return null;
  }

  return (
    <div>
      <strong>{label}</strong> {persons.join(", ")}
    </div>
  );
};

const EmailPreview = ({ email, onLogActivity, onView }) => {
  const { bcc, cc, created, from, snippet, subject, to, user } = email;

  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              alignItems: "center",
              display: "flex",
              marginBottom: "8px",
            }}
          >
            <Tag>
              <MailOutlined style={{ marginRight: "8px" }} />
              {detailed(created)}
            </Tag>
            <Metadata
              Icon={UserOutlined}
              style={{ marginLeft: "8px", marginRight: "0px" }}
              value={user.alias}
            />
            <Button
              ghost
              onClick={() => onLogActivity && onLogActivity(email)}
              type="primary"
              style={{ marginLeft: "auto" }}
            >
              Log Activity
            </Button>
            <Button
              ghost
              onClick={onView}
              type="primary"
              style={{ marginLeft: "8px" }}
            >
              View
            </Button>
          </div>
          <div>
            <strong>From</strong> {from}
          </div>
          <Persons label="To" persons={to} />
          <Persons label="cc" persons={cc} />
          <Persons label="bcc" persons={bcc} />
          <div>
            <strong>Subject</strong> {subject}
          </div>
        </div>
      </div>
      <div style={{ marginBottom: "16px" }}>
        <span style={{ ...PRIMARY_TEXT }}>{snippet}</span>
      </div>
    </>
  );
};

const EventPreview = ({ event, onLogActivity, onScheduleFollowup, user }) => {
  const { attendees, end, location, organizer, subject, webLink } = event;

  const hasHappened = end ? !day.unix(end).isAfter(day()) : true;

  const action = hasHappened
    ? {
        cb: onLogActivity,
        label: "Log Activity",
      }
    : {
        cb: onScheduleFollowup,
        label: "Schedule Followup",
      };

  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              marginBottom: "8px",
            }}
          >
            <Tag>
              <Tooltip title="Calendar">
                <CalendarOutlined style={{ marginRight: "8px" }} />
              </Tooltip>
              {detailed(end)}
            </Tag>
            <Metadata
              Icon={UserOutlined}
              style={{ marginLeft: "8px", marginRight: "0px" }}
              value={user.alias}
            />
            <Button
              ghost
              onClick={() => action.cb(event)}
              type="primary"
              style={{ marginLeft: "auto" }}
            >
              {action.label}
            </Button>
            <Button
              ghost
              onClick={() => {
                if (webLink)
                  window.open(webLink, "_blank", "noopener,noreferrer");
              }}
              type="primary"
              style={{ marginLeft: "8px" }}
              disabled={!webLink}
            >
              View
            </Button>
          </div>
          <div>
            <strong>Subject</strong> {subject}
          </div>
          {!isEmpty(organizer) && (
            <div>
              <strong>Organizer</strong> {organizer.join(", ")}
            </div>
          )}
          {!isEmpty(attendees) && (
            <div>
              <strong>Attendees</strong> {attendees.join(", ")}
            </div>
          )}
          {!isEmpty(location) && (
            <div>
              <strong>Location</strong> {location}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const FullEmail = ({ email, onClose, onLogActivity }) => {
  const { bcc, body, cc, created, from, subject, to, user } = email;

  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              alignItems: "center",
              display: "flex",
              marginBottom: "8px",
            }}
          >
            <Tag>
              <Tooltip title="Email">
                <MailOutlined style={{ marginRight: "8px" }} />
              </Tooltip>
              {detailed(created)}
            </Tag>
            <Tag>
              <MailOutlined style={{ marginRight: "8px" }} />
              Email
            </Tag>
            <Metadata
              Icon={UserOutlined}
              style={{ marginLeft: "8px", marginRight: "0px" }}
              value={user.alias}
            />
            <Button
              ghost
              onClick={() => {
                if (onLogActivity) {
                  onClose();
                  onLogActivity(email);
                }
              }}
              type="primary"
              style={{ marginLeft: "auto", marginRight: "32px" }}
            >
              Log Activity
            </Button>
          </div>
          <div>
            <strong>From</strong> {from}
          </div>
          <Persons label="To" persons={to} />
          <Persons label="cc" persons={cc} />
          <Persons label="bcc" persons={bcc} />
          <div>
            <strong>Subject</strong> {subject}
          </div>
        </div>
      </div>
      <div
        dangerouslySetInnerHTML={{ __html: body }}
        style={{ overflowY: "scroll", ...PRIMARY_TEXT }}
      />
    </>
  );
};

const FullEmailModal = ({
  email,
  onClose,
  onLoadMore,
  onLogActivity,
  onNext,
  onPrevious,
}) => {
  return (
    <Modal
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "50vh",
          minHeight: "500px",
          padding: "24px",
        },
      }}
      footer={
        <div>
          <Button disabled={!onPrevious} onClick={onPrevious}>
            Previous
          </Button>
          <Button
            disabled={!onNext && !onLoadMore}
            onClick={onNext || onLoadMore}
          >
            {!onNext && onLoadMore ? "Load" : "Next"}
          </Button>
        </div>
      }
      onCancel={onClose}
      open={!!email}
    >
      {email && (
        <FullEmail
          email={email}
          onClose={onClose}
          onLogActivity={onLogActivity}
        />
      )}
    </Modal>
  );
};

const Header = ({
  allowFuture,
  canSearchEmails,
  canCreateEvents,
  onCreateEvent,
  onRangeChange,
  onShiftRange,
  emailSearchUsers,
  totalActivityCount,
  visibleActivityCount,
  range,
  refreshEmails,
  refreshEvents,
  showPerformActivity,
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <div
        style={{
          alignItems: "flex-start",
          display: "flex",
          justifyContent: "space-between",
          gap: "16px",
          width: "100%",
        }}
      >
        <div>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              lineHeight: "1",
            }}
          >
            <h3 style={{ margin: "0px 8px 2px 0px" }}>Activity Timeline</h3>
            <div style={{ color: "rgba(0, 0, 0, 0.45)", fontSize: "12px" }}>
              Showing {visibleActivityCount} of {totalActivityCount} activities
            </div>
            {canSearchEmails && (
              <Tooltip
                title={
                  <div>
                    Searching across integrated email(s)...
                    <br />
                    <br />
                    {emailSearchUsers.join(", ")}
                  </div>
                }
              >
                <InfoCircleOutlined style={{ marginRight: "8px" }} />
              </Tooltip>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {showPerformActivity && (
            <Button ghost onClick={showPerformActivity} type="primary">
              Log Activity
            </Button>
          )}

          {canCreateEvents && (
            <Button ghost onClick={onCreateEvent} type="primary">
              Create Calendar Event
            </Button>
          )}
        </div>
      </div>

      <div
        style={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <Button
          ghost
          icon={<LeftOutlined />}
          onClick={() => onShiftRange(-1)}
          shape="circle"
          size="small"
          type="primary"
        />
        <RangePicker
          allowClear={false}
          disabledDate={(current) => {
            if (!allowFuture) {
              return current && current > day().endOf("day");
            }

            // cap far future to keep requests reasonable
            return current && current > day().add(1, "year").endOf("day");
          }}
          placeholder={["Start Time", "End Time"]}
          onChange={onRangeChange}
          size="small"
          value={range}
        />
        <Button
          ghost
          icon={<RightOutlined />}
          onClick={() => onShiftRange(1)}
          shape="circle"
          size="small"
          type="primary"
        />
        <RangeFilter
          allowFuture={allowFuture}
          setRange={onRangeChange}
        />
        <Button
          ghost
          icon={<SyncOutlined />}
          onClick={() => {
            refreshEmails();
            refreshEvents && refreshEvents();
          }}
          shape="circle"
          size="small"
          type="primary"
        />
      </div>
    </div>
  );
};

export const ActivityTimeline = ({
  activities = [],
  accountId,
  contactIds = [],
  contactId,
  onLogCalendarEventActivity,
  onLogEmailActivity,
  onScheduleCalendarFollowup,
  opportunityId,
  showPerformActivity,
  vendorId,
}) => {
  const { getToken } = useAuth();

  const users = useUsers().all();
  const profile = useMe();
  const organization = useOrganization();

  const contacts = useContacts().byIds(contactIds);
  const previousContacts = usePrevious(contacts);
  const previousUsers = usePrevious(users);

  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [cursors, setCursors] = useState([]);
  const [eventCursors, setEventCursors] = useState([]);
  const [emails, setEmails] = useState();
  const [emailsRangeKey, setEmailsRangeKey] = useState();
  const [events, setEvents] = useState();
  const [eventsRangeKey, setEventsRangeKey] = useState();
  const [emailIndex, setEmailIndex] = useState(-1);
  const [range, setRange] = useState(getDefaultRange());
  const [hasCustomRange, setHasCustomRange] = useState(false);
  const [displayCount, setDisplayCount] = useState(25);
  const emailRequestIdRef = useRef(0);
  const eventRequestIdRef = useRef(0);

  const contactEmails = useMemo(() => {
    return (contacts || [])
      .map((c) => c?.email)
      .filter((e) => typeof e === "string" && e.length > 0);
  }, [contacts]);
  const rangeKey = useMemo(() => getRangeKey(range), [range]);
  const rangeKeyRef = useRef(rangeKey);

  useEffect(() => {
    rangeKeyRef.current = rangeKey;
  }, [rangeKey]);

  const canCreateEvents =
    get(profile, "integrations.microsoft.isConfigured") &&
    get(
      profile,
      "integrations.microsoft.settings.features.calendar.permission"
    ) === "write";

  const createEventModal = useModal(CreateCalendarEventModal);

  const openCreateEvent = () => {
    createEventModal.open({
      accountId,
      contactEmails,
      contactId,
      opportunityId,
      profileId: profile?._id,
      vendorId,
      onCreated: async () => {
        refreshEvents();
      },
    });
  };

  const canSearchEmails =
    !isEmpty(
      filter(users, (user) => get(user, "integrations.microsoft.isConfigured"))
    ) && !isEmpty(filter(contacts, (contact) => contact.email));

  const canSearchEvents =
    !isEmpty(
      filter(users, (user) => {
        return (
          get(user, "integrations.microsoft.isConfigured") &&
          ["read", "write"].includes(
            get(
              user,
              "integrations.microsoft.settings.features.calendar.permission"
            )
          )
        );
      })
    ) && !isEmpty(filter(contacts, (contact) => contact.email));

  const allowFuture = canSearchEvents;

  useEffect(() => {
    if (hasCustomRange) {
      return;
    }

    const nextRange = getInitialRange({ activities, allowFuture });

    setRange((currentRange) => {
      return isSameRange(currentRange, nextRange) ? currentRange : nextRange;
    });
  }, [activities, allowFuture, hasCustomRange]);

  const emailSearchUsers = useMemo(() => {
    if (!profile) return [];

    const filteredUsers = users.filter((user) => {
      return (
        get(user, "integrations.microsoft.isConfigured") &&
        (profile._id === user._id ||
          get(user, "integrations.microsoft.settings.features.email.isPublic"))
      );
    });

    return filteredUsers.map((user) => user?.integrations?.microsoft?.email);
  }, [profile, users]);

  const userEmails = useMemo(
    () => cursors.map(({ userEmail }) => userEmail),
    [cursors]
  );

  const refreshEmails = () => {
    emailRequestIdRef.current += 1;
    setCursors([]);
    setEmails();
    setEmailsRangeKey();
    setEmailIndex(-1);
    setIsLoadingEmails(false);
  };

  const refreshEvents = () => {
    eventRequestIdRef.current += 1;
    setEventCursors([]);
    setEvents();
    setEventsRangeKey();
    setIsLoadingEvents(false);
  };

  const searchEmails = async () => {
    if (!canSearchEmails) return;

    const requestId = ++emailRequestIdRef.current;
    const requestRangeKey = rangeKey;

    setIsLoadingEmails(true);

    let updatedCursors = await Promise.all(
      map(users, async (user) => {
        let cursor = find(cursors, { userId: user._id });

        if (cursor && !cursor.nextPage) {
          return;
        }

        if (
          !get(user, "integrations.microsoft.isConfigured") ||
          !get(
            user,
            "integrations.microsoft.settings.features.email.isEnabled",
            true
          ) ||
          (profile._id !== user._id &&
            !get(
              user,
              "integrations.microsoft.settings.features.email.isPublic"
            ))
        ) {
          return;
        }

        const params = getEmailParams({
          contacts,
          page: get(cursor, "nextPage"),
          range,
          user,
        });

        try {
          const response = await fetch(
            `${
              import.meta.env.VITE_API_URL
            }/integrations/microsoft-graph/emails?${params}`,
            {
              headers: {
                Authorization: `Bearer ${await getToken()}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(
              `Failed to retrieve email history for ${user.email}.`
            );
          }

          if (response.status < 300) {
            const data = await response.json();

            let nextEmails = uniqBy(
              [...get(cursor, "emails", []), ...(data.emails || [])],
              "id"
            );

            nextEmails = reverse(sortBy(nextEmails, "created"));
            nextEmails = map(nextEmails, (email) => ({ ...email, user }));

            return {
              emails: nextEmails,
              nextPage: data.nextPage,
              userId: user._id,
              userEmail: user.email,
            };
          }
        } catch (err) {
          console.error(err);
        }
      })
    );

    updatedCursors = compact(updatedCursors);

    let updatedEmails = map(
      updatedCursors,
      ({ emails: userEmails }) => userEmails
    );
    updatedEmails = flatten(updatedEmails);
    updatedEmails = uniqBy(updatedEmails, "id");
    updatedEmails = reverse(sortBy(updatedEmails, "created"));

    if (
      requestId !== emailRequestIdRef.current ||
      requestRangeKey !== rangeKeyRef.current
    ) {
      if (requestId === emailRequestIdRef.current) {
        setIsLoadingEmails(false);
      }

      return;
    }

    setCursors(updatedCursors);
    setEmails(updatedEmails);
    setEmailsRangeKey(requestRangeKey);
    setIsLoadingEmails(false);
  };

  const searchEvents = async () => {
    if (!canSearchEvents) return;

    const requestId = ++eventRequestIdRef.current;
    const requestRangeKey = rangeKey;

    setIsLoadingEvents(true);

    let updatedCursors = await Promise.all(
      map(users, async (user) => {
        let cursor = find(eventCursors, { userId: user._id });

        if (cursor && !cursor.nextPage) {
          return;
        }

        if (
          !get(user, "integrations.microsoft.isConfigured") ||
          !["read", "write"].includes(
            get(
              user,
              "integrations.microsoft.settings.features.calendar.permission"
            )
          ) ||
          (profile._id !== user._id &&
            !get(
              user,
              "integrations.microsoft.settings.features.calendar.isPublic"
            ))
        ) {
          return;
        }

        const params = getEventParams({
          contacts,
          page: get(cursor, "nextPage"),
          range,
          user,
        });

        try {
          const response = await fetch(
            `${
              import.meta.env.VITE_API_URL
            }/integrations/microsoft-graph/events?${params}`,
            {
              headers: {
                Authorization: `Bearer ${await getToken()}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(
              `Failed to retrieve calendar events for ${user.email}.`
            );
          }

          if (response.status < 300) {
            const data = await response.json();
            let nextEvents = uniqBy(
              [...get(cursor, "events", []), ...(data.events || [])],
              "id"
            );

            nextEvents = sortBy(nextEvents, "end");
            nextEvents = map(nextEvents, (event) => ({ ...event, user }));

            return {
              events: nextEvents,
              nextPage: data.nextPage,
              userId: user._id,
              userEmail: user.email,
            };
          }
        } catch (err) {
          console.error(err);
        }
      })
    );

    updatedCursors = compact(updatedCursors);

    let updatedEvents = map(
      updatedCursors,
      ({ events: userEvents }) => userEvents
    );
    updatedEvents = flatten(updatedEvents);
    updatedEvents = uniqBy(updatedEvents, "id");
    updatedEvents = sortBy(updatedEvents, "end");

    if (
      requestId !== eventRequestIdRef.current ||
      requestRangeKey !== rangeKeyRef.current
    ) {
      if (requestId === eventRequestIdRef.current) {
        setIsLoadingEvents(false);
      }

      return;
    }

    setEventCursors(updatedCursors);
    setEvents(updatedEvents);
    setEventsRangeKey(requestRangeKey);
    setIsLoadingEvents(false);
  };

  useEffect(() => {
    if (!isSame(contacts, previousContacts) || !isSame(users, previousUsers)) {
      refreshEmails();
      refreshEvents();
      setHasCustomRange(false);
      setRange(getInitialRange({ activities, allowFuture }));
      setDisplayCount(25);
    }
  }, [activities, contacts, users, previousContacts, previousUsers, allowFuture]);

  useEffect(() => {
    if (!profile) return;

    if (canSearchEmails && emailsRangeKey && emailsRangeKey !== rangeKey) {
      refreshEmails();
      return;
    }

    if (canSearchEvents && eventsRangeKey && eventsRangeKey !== rangeKey) {
      refreshEvents();
      return;
    }

    if (canSearchEmails && emailsRangeKey !== rangeKey && !isLoadingEmails) {
      searchEmails();
      return;
    }

    if (canSearchEvents && eventsRangeKey !== rangeKey && !isLoadingEvents) {
      searchEvents();
      return;
    }

    if ((!isNil(emails) || !isNil(emailsRangeKey)) && !canSearchEmails) {
      refreshEmails();
      return;
    }

    if ((!isNil(events) || !isNil(eventsRangeKey)) && !canSearchEvents) {
      refreshEvents();
      return;
    }
  }, [
    canSearchEmails,
    canSearchEvents,
    emails,
    emailsRangeKey,
    events,
    eventsRangeKey,
    isLoadingEmails,
    isLoadingEvents,
    profile,
    rangeKey,
  ]);

  const filteredActivities = useMemo(() => {
    if (!range?.[0] || !range?.[1]) {
      return activities || [];
    }

    const start = range[0].startOf("day");
    const end = range[1].endOf("day");

    return (activities || []).filter((activity) => {
      if (isNil(activity?.performedOn)) {
        return false;
      }

      const performedOn = day.unix(activity.performedOn);

      return performedOn.isBetween(start, end, null, "[]");
    });
  }, [activities, range]);

  const merged = useMemo(() => {
    const emailItems = (emails || []).map((email) => ({
      id: `email:${email.id}`,
      kind: "email",
      timestamp: email.created,
      email,
    }));

    const activityItems = filteredActivities.map((activity) => ({
      id: `activity:${activity._id}`,
      kind: "activity",
      timestamp: activity.performedOn,
      activity,
    }));

    const eventItems = (events || []).map((event) => ({
      id: `event:${event.id}`,
      kind: "event",
      timestamp: event.end,
      event,
    }));

    return [...emailItems, ...activityItems, ...eventItems].sort(
      (a, b) => b.timestamp - a.timestamp
    );
  }, [emails, events, filteredActivities]);

  const visible = merged.slice(0, displayCount);
  const canFetchMoreEmails = !isEmpty(
    filter(cursors, (cursor) => cursor.nextPage)
  );
  const canFetchMoreEvents = !isEmpty(
    filter(eventCursors, (cursor) => cursor.nextPage)
  );
  const canLoadMore =
    displayCount < merged.length ||
    (canSearchEmails && canFetchMoreEmails) ||
    (canSearchEvents && canFetchMoreEvents);

  const loadMore = canLoadMore && (
    <div
      style={{
        height: 32,
        lineHeight: "32px",
        marginTop: 12,
        textAlign: "center",
      }}
    >
      <Button
        disabled={isLoadingEmails || isLoadingEvents}
        ghost
        onClick={async () => {
          setDisplayCount((c) => c + 25);

          if (displayCount >= merged.length) {
            if (canFetchMoreEmails) {
              await searchEmails();
            }
            if (canFetchMoreEvents) {
              await searchEvents();
            }
          }
        }}
        type="primary"
      >
        Load More
      </Button>
    </div>
  );

  const onNext =
    emails &&
    emailIndex < emails.length - 1 &&
    (() => setEmailIndex(emailIndex + 1));

  const onPrevious =
    emails && emailIndex > 0 && (() => setEmailIndex(emailIndex - 1));

  const onLoadMoreInModal =
    !onNext &&
    canFetchMoreEmails &&
    (() => {
      setEmailIndex(-1);
      searchEmails();
    });

  const onLogEmail = (email) => {
    if (onLogEmailActivity) {
      onLogEmailActivity(email, userEmails);
    }
  };

  const onRangeChange = (nextRange) => {
    setHasCustomRange(true);
    setRange(nextRange);
    refreshEmails();
    refreshEvents();
  };

  const onShiftRange = (direction) => {
    if (!range?.[0] || !range?.[1]) {
      return;
    }

    const spanInDays = Math.max(range[1].startOf("day").diff(range[0].startOf("day"), "day"), 0) + 1;
    const nextRange = [
      range[0].add(spanInDays * direction, "day"),
      range[1].add(spanInDays * direction, "day"),
    ];

    onRangeChange(nextRange);
  };

  const selectedEmail = emails && emails[emailIndex];

  if (!profile || !users) {
    return null;
  }

  if (
    (canSearchEmails && isNil(emails)) ||
    (canSearchEvents && isNil(events))
  ) {
    return (
      <Card
        bordered={false}
        bodyStyle={{
          display: "flex",
          justifyContent: "center",
          padding: "48px",
        }}
      >
        <Spin />
      </Card>
    );
  }

  return (
    <>
      <FullEmailModal
        email={selectedEmail}
        onClose={() => setEmailIndex(-1)}
        onLoadMore={onLoadMoreInModal}
        onLogActivity={onLogEmail}
        onNext={onNext}
        onPrevious={onPrevious}
      />
      <Card bordered={false} bodyStyle={{ paddingTop: "0px" }}>
        <List
          className={classes.list}
          dataSource={visible}
          header={
            <Header
              allowFuture={allowFuture}
              canSearchEmails={canSearchEmails}
              canSearchEvents={canSearchEvents}
              canCreateEvents={canCreateEvents}
              onCreateEvent={openCreateEvent}
              onRangeChange={onRangeChange}
              onShiftRange={onShiftRange}
              emailSearchUsers={emailSearchUsers}
              totalActivityCount={activities.length}
              visibleActivityCount={filteredActivities.length}
              range={range}
              refreshEmails={refreshEmails}
              refreshEvents={refreshEvents}
              showPerformActivity={showPerformActivity}
            />
          }
          itemLayout={"vertical"}
          loading={isLoadingEmails || isLoadingEvents}
          loadMore={loadMore}
          locale={{
            emptyText: <Empty description={"No activity yet"} />,
          }}
          renderItem={(item) => {
            return (
              <List.Item key={item.id}>
                {item.kind === "activity" ? (
                  <div className={classes.item}>
                    <Activity
                      activity={item.activity}
                      organization={organization}
                    />
                  </div>
                ) : item.kind === "email" ? (
                  <div className={classes.item}>
                    <EmailPreview
                      email={item.email}
                      onLogActivity={onLogEmail}
                      onView={() => {
                        const index = (emails || []).findIndex(
                          (e) => e.id === item.email.id
                        );
                        setEmailIndex(index);
                      }}
                    />
                  </div>
                ) : (
                  <div className={classes.item}>
                    <EventPreview
                      event={item.event}
                      user={item.event.user}
                      onLogActivity={(event) => {
                        if (onLogCalendarEventActivity) {
                          onLogCalendarEventActivity(event);
                        }
                      }}
                      onScheduleFollowup={(event) => {
                        if (onScheduleCalendarFollowup) {
                          onScheduleCalendarFollowup(event);
                        }
                      }}
                    />
                  </div>
                )}
              </List.Item>
            );
          }}
        />
      </Card>
      {createEventModal.modal}
    </>
  );
};
